/**
 * The pure half of sending mail from the platform: reading the addresses a person types
 * and saying where a sending stands. «Enviar por correo» of a quote (contract v10.26) and
 * the mail manager (contract v10.40) share it, so it lives here and not in either
 * feature. Out of the components so the tests pin the words without rendering.
 */

/**
 * What was typed or pasted, one recipient a piece: split on semicolons and lines first,
 * then on commas, except a comma inside a name. A list copied from Outlook reads
 * «Pérez García, Ana <ana@x.es>; Gil, Luis <luis@x.es>», and its first piece is one
 * person, not «Pérez García» and «Ana <ana@x.es>». So a piece with no address of its own
 * goes back in front of the next one when that one is a «Nombre <dirección>».
 */
export function recipientPieces(text: string): string[] {
  const out: string[] = [];
  for (const segment of text.split(/[;\n]+/)) {
    let name = '';
    for (const piece of segment.split(',')) {
      if (name && /<[^<>]*@[^<>]*>/.test(piece)) {
        out.push(`${name},${piece}`);
        name = '';
        continue;
      }
      if (name) out.push(name);
      name = '';
      if (!piece.includes('@') && !piece.includes('<') && piece.trim()) name = piece;
      else out.push(piece);
    }
    if (name) out.push(name);
  }
  return out.map((piece) => piece.trim()).filter(Boolean);
}

/** One recipient as it was written: «Ana Pérez <ana@x.es>» is the address with a name. */
export interface WrittenAddress {
  address: string;
  name: string | null;
}

/**
 * The recipients in what was typed or pasted (`recipientPieces`), each once whatever its
 * case. «Nombre <dirección>» is that address with its name, quotes off; without brackets,
 * spaces separate addresses too («a@x.es b@y.es»), and a stray word stays, to be flagged.
 */
export function writtenAddresses(text: string): WrittenAddress[] {
  const seen = new Set<string>();
  const out: WrittenAddress[] = [];
  for (const piece of recipientPieces(text)) {
    // An address being typed has no «>» yet: what follows «<» is still the address.
    const bracketed = /^([^<]*)<([^<>]*)>?/.exec(piece);
    const found = bracketed
      ? [{ address: bracketed[2] ?? '', name: nameOf(bracketed[1] ?? '') }]
      : piece.split(/\s+/).map((address) => ({ address, name: null }));
    for (const each of found) {
      const address = each.address.trim();
      if (!address) continue;
      const key = address.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ address, name: each.name });
    }
  }
  return out;
}

function nameOf(text: string): string | null {
  const name = text
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim();
  return name || null;
}

/**
 * The addresses in what was typed or pasted, as `writtenAddresses` reads them, without
 * the names. The back drops repeats too; doing it here keeps the field honest about what
 * will be sent.
 */
export function addressesFrom(text: string): string[] {
  return writtenAddresses(text).map((each) => each.address);
}

/** The first thing that does not look like an address, or null when all do. */
export function notAnAddress(addresses: string[]): string | null {
  return addresses.find((address) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) ?? null;
}

/** «ana@x.es», «ana@x.es y luis@y.es», «ana@x.es, luis@y.es y 2 más». */
export function recipientsText(addresses: string[]): string {
  if (addresses.length <= 1) return addresses[0] ?? '';
  if (addresses.length === 2) return `${addresses[0]} y ${addresses[1]}`;
  return `${addresses.slice(0, 2).join(', ')} y ${addresses.length - 2} más`;
}

/** After this long without an answer about the copy in Sent, stop waiting for it. */
export const APPEND_WAIT_MS = 2 * 60_000;

/**
 * What the screen needs of a sending, whichever endpoint it came from: a quote's
 * (`QuoteSendingRead`) or the mail manager's (`OutgoingRead`). Both are written by the
 * worker the same way (ADR 0022 of the back): `pending`, then `sent` or `failed`, and the
 * copy in Sent after.
 */
export interface MailSending {
  status: 'pending' | 'sent' | 'failed';
  error?: string | null;
  sent_at?: string | null;
  appended_at?: string | null;
  append_error?: string | null;
  created_at: string;
}

export type SendingState =
  | { kind: 'sending' }
  | { kind: 'failed'; reason: string }
  | { kind: 'sent'; copy: 'saved' | 'saving' | 'failed' | 'unknown'; copyError: string | null };

/**
 * Where one sending stands: on its way, refused, or sent, and then what happened to
 * the copy in the mailbox's Sent folder, which is written after the send and can fail
 * on its own without the recipient missing anything.
 */
export function sendingState(sending: MailSending, now = Date.now()): SendingState {
  if (sending.status === 'pending') return { kind: 'sending' };
  if (sending.status === 'failed') {
    return { kind: 'failed', reason: sending.error ?? 'el servidor de correo no lo aceptó' };
  }
  if (sending.appended_at) return { kind: 'sent', copy: 'saved', copyError: null };
  if (sending.append_error) {
    return { kind: 'sent', copy: 'failed', copyError: sending.append_error };
  }
  const since = Date.parse(sending.sent_at ?? sending.created_at);
  return {
    kind: 'sent',
    copy: Number.isFinite(since) && now - since < APPEND_WAIT_MS ? 'saving' : 'unknown',
    copyError: null,
  };
}

/** Whether the screen has something to wait for: a sending on its way, or its copy. */
export function waitingOnSending(
  sendings: readonly MailSending[] | null | undefined,
  now = Date.now(),
): boolean {
  return (sendings ?? []).some((sending) => {
    const state = sendingState(sending, now);
    return state.kind === 'sending' || (state.kind === 'sent' && state.copy === 'saving');
  });
}
